import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// Lazy load modules for code splitting
const Dashboard = () => import('@/modules/dashboard/DashboardView.vue')
const Zones = () => import('@/modules/zones/ZonesView.vue')
const ZoneDetail = () => import('@/modules/zones/ZoneDetailView.vue')
const Records = () => import('@/modules/records/RecordsView.vue')
const Servers = () => import('@/modules/servers/ServersView.vue')
const Recursor = () => import('@/modules/recursor/RecursorView.vue')
const Analytics = () => import('@/modules/analytics/AnalyticsView.vue')
const Audit = () => import('@/modules/audit/AuditView.vue')
const Settings = () => import('@/modules/settings/SettingsView.vue')
const ApiDocs = () => import('@/modules/docs/ApiDocsView.vue')
const Login = () => import('@/modules/auth/LoginView.vue')
const NotFound = () => import('@/components/common/NotFound.vue')

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { public: true, layout: 'auth' },
  },
  {
    path: '/',
    name: 'Dashboard',
    component: Dashboard,
    meta: { title: 'Dashboard', icon: 'ChartBarIcon' },
  },
  {
    path: '/zones',
    name: 'Zones',
    component: Zones,
    meta: { title: 'Zonas DNS', icon: 'GlobeAltIcon' },
  },
  {
    path: '/zones/:id',
    name: 'ZoneDetail',
    component: ZoneDetail,
    meta: { title: 'Detalhes da Zona', parent: 'Zones' },
  },
  {
    path: '/records',
    name: 'Records',
    component: Records,
    meta: { title: 'Registros', icon: 'DocumentTextIcon' },
  },
  {
    path: '/servers',
    name: 'Servers',
    component: Servers,
    meta: { title: 'Servidores', icon: 'ServerStackIcon' },
  },
  {
    path: '/recursor',
    name: 'Recursor',
    component: Recursor,
    meta: { title: 'Recursor', icon: 'ArrowPathRoundedSquareIcon' },
  },
  {
    path: '/analytics',
    name: 'Analytics',
    component: Analytics,
    meta: { title: 'Analytics', icon: 'ChartPieIcon' },
  },
  {
    path: '/audit',
    name: 'Audit',
    component: Audit,
    meta: { title: 'Auditoria', icon: 'ClipboardDocumentListIcon' },
  },
  {
    path: '/settings',
    name: 'Settings',
    component: Settings,
    meta: { title: 'Configurações', icon: 'Cog6ToothIcon' },
  },
  {
    path: '/docs',
    name: 'ApiDocs',
    component: ApiDocs,
    meta: { title: 'Documentação API', icon: 'BookOpenIcon' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound,
    meta: { public: true },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  },
})

// Navigation guards
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()
  
  // Update page title
  const title = to.meta.title ? `${to.meta.title} | XDNS Panel` : 'XDNS Panel'
  document.title = title

  // Check if route requires auth
  if (!to.meta.public && !authStore.isAuthenticated) {
    return next({ name: 'Login', query: { redirect: to.fullPath } })
  }

  // If authenticated and trying to access login, redirect to dashboard
  if (to.name === 'Login' && authStore.isAuthenticated) {
    return next({ name: 'Dashboard' })
  }

  next()
})

export default router
